/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  CaptureProcessingProgress,
  FolderImportStagingResult,
  LiveCaptureProgress,
  StagedCapture,
} from '../shared/desktop-api';
import { importArrived, takeArrived } from './arrived-setup';
import type { EngineCall } from './desktop-commands';
import { pickFiles, type PickedFiles } from './folder-picker';
import type { JobChannels } from './job-channels';
import { registerPageHold } from './page-holds';
import { PagesHeldError } from './pages-held';
import { FileReadTally, recordFilesRead, withFilesRead } from './file-read-tally';
import { captureProgress, progressFor, runProcess, type RawProgress } from './process-runs';
import { clearUploadLine, uploadFiles, type UploadSource } from './upload';
import { stageFolderWaves } from './wave-uploads';
import { readInWaves, readsInWaves, type ScannerWaveCursor, type WaveRun } from './scanner-waves';

export interface BrowserCaptures {
  /** The desktop API's capture methods, for the adapter. */
  methods: Record<string, unknown>;
  /** Uploads files into the job's workspace and stages the capture; the scan sheet's way in. */
  stageFiles: (
    jobId: string,
    files: File[],
    label: string,
    source?: UploadSource,
  ) => Promise<StagedCapture>;
  /** A folder for a job that has its setup, staged as CapturePoint's waves (folder-waves.ts). */
  stageWaves: (jobId: string, picked: PickedFiles) => Promise<FolderImportStagingResult>;
}

/**
 * Add pages from files, in the browser: the desktop staged a capture from its file dialog and its
 * shell drained the queue through the engine; here the browser's file picker uploads the files into
 * the job's workspace (`POST {channel}/import`, upload.ts), the capture is remembered in the page, and the
 * screen's own queue drain calls `processStagedCapture`, which becomes the engine's `process` over
 * the files the workspace now holds (docs/intake-and-account.md). Scanned pages take the same road
 * through `stageFiles`; a folder's waves are staged at once and go up one after another, each read
 * as soon as its own files are in (wave-uploads.ts).
 */
export function browserCaptures(call: EngineCall, channels: JobChannels): BrowserCaptures {
  guardLeaving();
  const stageFiles = staging(channels);
  const methods = {
    stageDocuments: async ({ jobId }: { jobId: string }) => {
      if (takeArrived()) return { canceled: false, capture: await stageArrived(channels, jobId) };
      const picked = await pickFiles();
      if (picked.length === 0) return { canceled: true };
      return { canceled: false, capture: await stageFiles(jobId, picked) };
    },
    // A processed capture is no longer waiting, so neither drain picks it up again.
    listStagedCaptures: async (jobId: string) =>
      [...captures.values()]
        .filter((c) => c.capture.jobId === jobId && c.result === undefined)
        .map((c) => c.capture),
    processStagedCapture: processStaged(call, captures),
    discardStagedCapture: async (captureId: string) => {
      const entry = captures.get(captureId);
      if (entry?.inFlight) throw new Error('These pages are being read now.');
      captures.delete(captureId);
      // Paige queued the upload's reading with it, for a browser that goes away; a discard takes it off.
      // The capture is gone from this page either way, so a failed call is not the person's problem.
      if (entry) {
        await call('discard-capture', { jobId: entry.capture.jobId, captureId }).catch(
          () => undefined,
        );
      }
    },
    retryStagedCapture: async (captureId: string) => retry(captureId),
    onCaptureProcessingProgress: captureProgress.subscribe,
    // What a job is reading while no screen watches it: a returning workspace opens on the reading
    // (opening-job.ts) instead of on "Add your first pages" while its first pages are being read.
    getLiveCaptureProgress: async (jobId: string) => liveProgress.get(jobId) ?? null,
  };
  const stageWaves = (jobId: string, picked: PickedFiles) =>
    stageFolderWaves(channels.channelFor(jobId), jobId, picked, (entry) =>
      captures.set(entry.capture.id, entry),
    );
  return { methods, stageFiles, stageWaves };
}

/**
 * The files up into the job's workspace, then the capture the queue drains. The ones Paige could
 * not read stay on the upload line for a moment on the way out: the rest of the selection went in
 * without them (ADR-0012), so this is where the operator learns which ones to send again.
 */
function staging(channels: JobChannels) {
  return async (
    jobId: string,
    files: File[],
    label?: string,
    source: UploadSource = 'upload',
  ): Promise<StagedCapture> => {
    const receipt = await uploadFiles(
      channels.channelFor(jobId),
      files,
      (progress) => window.paigeWorkbench?.upload?.(progress),
      source,
      // The scan sheet's pages (a label) are a scan stack; a person's chosen files are each one document.
      label !== undefined,
    ).catch((reason: unknown) => {
      window.paigeWorkbench?.upload?.(null);
      throw reason;
    });
    clearUploadLine({ sent: files.length, total: files.length, unreadable: receipt.skipped });
    if (receipt.files.length === 0) throw new Error('None of the chosen files are PDFs or images');
    const capture = stagedCapture(jobId, receipt.token, files, label);
    captures.set(capture.id, { capture, files: receipt.files });
    return capture;
  };
}

/**
 * The documents a program sent into a job nobody had set up, into the job's workspace as a capture
 * (arrived-setup.ts): the API moves them itself and answers an upload's receipt, so the queue drains
 * this capture as it drains one the person uploaded.
 */
async function stageArrived(channels: JobChannels, jobId: string): Promise<StagedCapture> {
  const receipt = await importArrived(channels.channelFor(jobId));
  if (receipt.files.length === 0)
    throw new Error('Nothing is waiting for this job any more. Choose documents to set it up.');
  const count = receipt.files.length;
  const capture: StagedCapture = {
    ...stagedCapture(jobId, receipt.token, []),
    sourceLabel:
      count === 1 ? '1 file that arrived' : `${count.toLocaleString()} files that arrived`,
    fileCount: count,
  };
  captures.set(capture.id, { capture, files: receipt.files });
  return capture;
}

export interface StagedEntry {
  capture: StagedCapture;
  /** The workspace's paths for the capture's files, once they are up. */
  files?: string[];
  /** The upload under way (a folder wave's), shared by every ask; cleared when it fails. */
  uploading?: Promise<string[]>;
  /** Sends the capture's files up (again, after a failure); a folder wave's only. */
  upload?: () => Promise<string[]>;
  /** The engine call under way, so two drains asking at once share one run. */
  inFlight?: Promise<unknown>;
  /** What the engine answered, so a second ask after the first finished gets the same answer. */
  result?: unknown;
  /** Where a long scanner load's waves have got to (scanner-waves.ts). */
  waves?: ScannerWaveCursor;
}
type Staged = Map<string, StagedEntry>;

/**
 * The page's staged captures, one registry for the page's lifetime: the adapter is installed again
 * whenever the workbench's props change (a route re-render is enough), and a capture staged
 * before that must still be there when the queue drains it, as the desktop's manifests were.
 */
const captures: Staged = new Map();

let leavingGuarded = false;

/**
 * Pages added but not read yet live in this page (the registry above), where the desktop kept them
 * in manifests on its disk and read them after a restart. So while any wait, closing or reloading
 * the tab asks first, as the desktop asks before it closes on work it would lose; a reading already
 * running on the host finishes either way, but the waves behind it would not be read. For the same
 * reason they hold the page against Paige reloading itself onto a newer version (page-holds.ts).
 */
function guardLeaving(): void {
  if (leavingGuarded || typeof window === 'undefined' || !window.addEventListener) return;
  leavingGuarded = true;
  registerPageHold('pages-waiting', pagesWaiting);
  window.addEventListener('beforeunload', (event) => {
    if (!pagesWaiting()) return;
    event.preventDefault();
    event.returnValue = '';
  });
}

/** Whether any capture is still to be read: not read yet, and not left failed for a person to decide. */
export function pagesWaiting(): boolean {
  return [...captures.values()].some(
    (entry) => entry.result === undefined && entry.capture.status !== 'failed',
  );
}

/** The latest counters of each job's capture being read, without the per-event payload. */
const liveProgress = new Map<string, LiveCaptureProgress>();

/** The files the workspace holds for a capture: at hand, or the wave's upload waited for. */
async function filesOf(staged: StagedEntry): Promise<string[]> {
  if (staged.files) return staged.files;
  if (!staged.upload) throw new Error('These pages are no longer waiting; add them again.');
  staged.uploading ??= staged.upload();
  try {
    staged.files = await staged.uploading;
    return staged.files;
  } catch (reason) {
    staged.uploading = undefined;
    throw reason;
  }
}

/**
 * The desktop's retry: a capture being read is refused, a failed one is pending again and is read
 * (and, for a wave whose upload failed, sent) again by the next drain. Answers the attempts so far.
 */
async function retry(captureId: string): Promise<number> {
  const staged = captures.get(captureId);
  if (!staged) return 0;
  if (staged.inFlight) throw new Error('These pages are already being read.');
  staged.capture = {
    ...staged.capture,
    status: 'pending',
    error: null,
    waitingForPages: undefined,
  };
  return staged.capture.attempts ?? 0;
}

/**
 * The queue drains: the engine's `process` over the files the workspace holds for this capture.
 * The screen's own queue and the app-lifetime drain both ask; the desktop's main process answered
 * both from one manifest, so here one run serves every ask and its result is kept for late ones.
 */
function processStaged(call: EngineCall, captures: Staged) {
  return async (request: any) => {
    const staged = captures.get(request.captureId);
    if (!staged) throw new Error('These pages are no longer waiting; add them again.');
    if (staged.result !== undefined) return staged.result;
    staged.inFlight ??= read(call, staged, request).finally(() => {
      staged.inFlight = undefined;
    });
    return staged.inFlight;
  };
}

/** One capture's reading: its files, the engine's run, its progress out to the screen. */
async function read(call: EngineCall, staged: StagedEntry, request: any): Promise<unknown> {
  const { captureId, ...rest } = request;
  const jobId = staged.capture.jobId;
  try {
    const files = await filesOf(staged);
    staged.capture = { ...staged.capture, attempts: (staged.capture.attempts ?? 0) + 1 };
    // The host answers a run; its progress is the screen's capture progress (process-runs.ts). A
    // folder wave's carries the files whose pages are all read (CapturePoint 64ee0c3b).
    const group = staged.capture.folderImport?.id;
    const tally = group ? new FileReadTally() : null;
    const run: WaveRun<{ consumedSourceCount?: number }> = (wave) =>
      runProcess(call, { ...rest, ...wave }, (raw) => {
        if (tally && group) recordFilesRead(group, captureId, tally.observe(raw));
        const shown = withFilesRead(group, raw) as RawProgress;
        publishProgress({ ...progressFor(shown, jobId), captureId });
      });
    // A long scanner load is read a wave at a time, each wave's documents in the job as it ends
    // (scanner-waves.ts, parity B-07); every other capture is one reading, as before.
    const result = readsInWaves(staged.capture.sourceKind, files.length)
      ? await readInWaves(captureId, files, staged, run)
      : await run({ files, acquisitionId: captureId });
    staged.result = result;
    return result;
  } catch (reason) {
    const error = reason instanceof Error ? reason.message : 'Recognition failed';
    // Held for pages, the API keeps the capture and reads it when more arrive (pages-held.ts): it waits.
    const waitingForPages = reason instanceof PagesHeldError ? true : undefined;
    staged.capture = { ...staged.capture, status: 'failed', error, waitingForPages };
    throw reason;
  } finally {
    if (liveProgress.get(jobId)?.captureId === captureId) liveProgress.delete(jobId);
  }
}

/** Out to the screen, and kept (counters only) for a screen that comes back while it runs. */
function publishProgress(progress: CaptureProcessingProgress): void {
  const counters: LiveCaptureProgress & Partial<CaptureProcessingProgress> = { ...progress };
  delete counters.activity;
  delete counters.completedDocument;
  liveProgress.set(progress.jobId, counters);
  captureProgress.publish(progress);
}

function stagedCapture(jobId: string, token: string, files: File[], label?: string): StagedCapture {
  return {
    id: token,
    jobId,
    sourceKind: label ? 'scanner' : 'files',
    sourceLabel:
      label ??
      (files.length === 1 ? files[0]!.name : `${files.length.toLocaleString()} selected files`),
    fileCount: files.length,
    status: 'pending',
    createdAt: new Date().toISOString(),
    error: null,
    attempts: 0,
  };
}
